import { createFileRoute, Link } from "@tanstack/react-router";
import { marketingHead } from "@/lib/marketing/seo";
import { LegalPage, type LegalSection } from "./-components/legal-page";

export const Route = createFileRoute("/legal/privacy")({
  component: PrivacyPolicy,
  head: () =>
    marketingHead({
      title: "Privacy policy | People AI",
      description:
        "What People AI and its Android app collect, why, who processes it, how long it is kept, and how to delete your account.",
      /* The v2 prefix is how a page says it belongs to the People AI site: canonical on
       * ppl-ai.com. */
      path: "/v2/legal/privacy",
    }),
});

const deleteLink = (
  <Link className="text-foreground underline underline-offset-4" to="/legal/delete-account">
    Delete your account
  </Link>
);

const SECTIONS: LegalSection[] = [
  {
    heading: "Who we are",
    paragraphs: [
      "People AI is software that assisted living communities use to run care, staff, families, billing and the front desk. It is run by PPL AI Inc. (“we”). The communities that subscribe are our customers, and the information in a community's account belongs to that community. We process it for them under our agreement, which includes a Business Associate Agreement under HIPAA where it covers health information.",
    ],
  },
  {
    heading: "What we collect",
    points: [
      "Your account: your name, email address or username, phone number, job, the community and buildings you work in, and your password and quick sign-in code, which are stored only in hashed form.",
      "What you and your colleagues enter: resident records such as care plans, medication records, notes and incident reports; staff records such as schedules, time and documents; and the contacts of families, leads and partners.",
      "Calls and texts: when your community uses the People AI phone or Sarah, its front desk agent, the numbers, times, recordings, transcripts and texts of those calls, as your community sets them up.",
      "Use and security: your IP address, browser and device type, sign-in times, and a log of who viewed or changed a record.",
    ],
  },
  {
    heading: "The People AI app for Android",
    points: [
      "The People AI app has its own screens for Aria, Sarah and your settings, and shows Core and Shelby as the website draws them. Both read the same account over an encrypted connection.",
      "You sign in on the website's own sign-in page, and the app keeps your sign-in on your phone so you do not have to sign in every time. Signing out in Settings ends it.",
      "It asks for the microphone only to make and take calls in the People AI phone, and only when you use it.",
      "It does not read your contacts, photos, location, messages or other apps. It has no advertising identifier and shows no ads.",
      "Signing up is done on the website, in your phone's browser.",
    ],
  },
  {
    heading: "How we use it",
    points: [
      "To provide People AI to your community and to you.",
      "To keep it secure: sign-in, the record of who did what, and spotting misuse.",
      "To send what the service needs to send, such as sign-in codes, notifications, and the texts and emails your community sends through it.",
      "To help you when you ask for help, and to fix problems.",
      "We do not sell personal information, and we do not use it for advertising.",
    ],
  },
  {
    heading: "Text messages",
    id: "text-messages",
    paragraphs: [
      "People AI sends texts for PPL AI Inc. and, for the assisted living communities that use it, on each community's behalf from that community's own numbers.",
    ],
    points: [
      "What is sent: sign-in and verification codes; replies and follow-ups to people who asked about People AI through our contact, demo or sign-up forms; and, for a community, replies and follow-ups to families and callers who contacted it, updates to job applicants who asked for them, and alerts to its own staff.",
      "How often: message frequency varies with your request or conversation.",
      "Cost: message and data rates may apply.",
      "To stop: reply STOP to any text to stop texts from that number, and START to receive them again. Reply HELP for help, or email info@ppl-ai.com.",
      "Agreeing to texts is never a condition of buying anything or of applying for a job.",
      "Mobile numbers and text message consent are never sold, and never shared with third parties or affiliates for their marketing. Our telephony provider delivers the texts for us and for nothing else.",
      "Carriers are not liable for delayed or undelivered messages.",
    ],
  },
  {
    heading: "Who processes it for us",
    paragraphs: [
      "We share information only with your community and with the service providers that run People AI for us, under contracts that limit them to that work: cloud hosting and databases, file storage, email delivery, telephony (such as Twilio), payments (Stripe), maps (Google), AI model providers for Aria, the assistant, and for Sarah, and error and quality monitoring. We also disclose information when the law requires it.",
    ],
  },
  {
    heading: "How long we keep it",
    points: [
      "Your account is kept while you have access to your community, or until you ask us to delete it.",
      "Records made for residents are kept for as long as your community's agreement and the law require, which for health records is often several years.",
      "When a community leaves People AI, its information is returned or deleted as its agreement says.",
    ],
  },
  {
    heading: "How we protect it",
    points: [
      "Every connection is encrypted (HTTPS).",
      "Access depends on your role and the buildings you work in, and every view and change of a record is logged.",
      "Passwords and quick sign-in codes are hashed, sign-in is rate limited, and you are signed out after a period without activity that your building sets (2 hours unless it changes it).",
    ],
  },
  {
    heading: "Your choices",
    paragraphs: [
      "You can see and correct your own details in Settings, Profile. Your community's administrator can correct the rest. Residents and families should contact their community, which decides how their information is used.",
      {
        key: "delete",
        node: (
          <>
            You can ask for your account to be deleted from inside the app or by email: {deleteLink}
            .
          </>
        ),
      },
      "People AI is for adults at work and is not meant for children.",
    ],
  },
  {
    heading: "Changes",
    paragraphs: [
      "When we change this policy we update this page and the date at the top. For a significant change we tell communities before it takes effect.",
    ],
  },
];

function PrivacyPolicy() {
  return (
    <LegalPage
      title="People AI privacy policy"
      updated="13 October 2026"
      intro="This policy covers People AI: the website at ppl-ai.com (and assisted-care.com, its former address) and the People AI app for Android."
      sections={SECTIONS}
    />
  );
}
